import * as React from "react";

import {
  IFluid,
  ILocalFile
} from "../../types/local-files";
import { IProfileFields } from "../../pages/about-us";
import styled from "../../theme/styled";
import {
  colourHelper,
  fontLineHeightHelper,
  fontSizeHelper,
  fontSpaceHelper,
  fontWeightHelper
} from "../../theme/theme-helper";
import AuthorSocialLinks from "../author-social-links/author-social-links";
import {
  HalfColumnWidthWrapper,
  SmallColumnWidthWrapper
} from "../confines-wrappers";
import DynamicImage from "../dynamic-image/dynamic-image";
import Link from "../link";

type IArticleType = "latest" | "insights";

interface IProps {
  type: IArticleType;
  profile: IProfileFields;
}

interface IArticleAuthorWrapper {
  hasMugshot: boolean;
}

interface IMugshot {
  data?: ILocalFile<IFluid>;
  alt: string;
  path: string;
}

interface IMoreLink {
  type: IArticleType;
  authorName: string;
  path: string;
}

const ArticleAuthorWrapper = styled<IArticleAuthorWrapper, "div">("div")`
  .hr {
    display: block;

    span {
      display: block;
      height: 0.2rem;
      background-color: ${({ theme }) =>
    colourHelper({
      theme,
      variant: "lightestGrey"
    })};
    }
  }

  .author-wrapper {
    transition: 0.2s ease-in-out;
    display: flex;
    flex-direction: row;
    align-items: center;
    padding-top: 2.8rem;
    min-height: ${props => (props.hasMugshot ? "17.8rem" : "0")};

    .author {
      transition: 0.2s ease-in-out;
      position: relative;
      padding-top: 0;
      padding-left: ${props => (props.hasMugshot ? "10rem" : "0")};
      width: 100%;

      .author-text {
        margin-bottom: 0;
        line-height: ${({ theme }) =>
    fontLineHeightHelper({
      theme,
      variant: "normalized"
    })};

        span {
          display: block;
          line-height: ${({ theme }) =>
    fontLineHeightHelper({
      theme,
      variant: "small"
    })};
        }

        .written-by,
        .read-more {
          font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "small_3"
    })};
          font-weight: ${({ theme }) =>
    fontWeightHelper({
      theme,
      variant: "light"
    })};
        }

        .written-by,
        .author-role {
          color: ${({ theme }) =>
    colourHelper({
      theme,
      variant: "lightGrey"
    })};
        }

        .written-by {
          position: absolute;
          left: -99999rem;
          text-indent: -99999rem;
        }

        .author-name {
          font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "subhead_1"
    })};
          font-weight: ${({ theme }) =>
    fontWeightHelper({
      theme,
      variant: "heavy"
    })};

          a {
            color: ${({ theme }) => colourHelper({
    theme,
    variant: "black"
  })};

            &:hover {
              color: ${({ theme }) =>
    colourHelper({
      theme,
      variant: "brandColor"
    })};
            }
          }
        }

        .author-role {
          padding-top: 0.4rem;
          font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "small_1"
    })};
          line-height: ${({ theme }) =>
    fontLineHeightHelper({
      theme,
      variant: "normalized"
    })};
          letter-spacing: ${({ theme }) =>
    fontSpaceHelper({
      theme,
      variant: "medium"
    })};
          text-transform: uppercase;
        }

        .read-more {
          padding-top: ${({ theme }) =>
    fontLineHeightHelper({
      theme,
      variant: "small"
    })};
        }
      }

      .author-image-wrapper {
        transition: 0.2s ease-in-out;
        position: absolute;
        z-index: 2;
        top: -1.8rem;
        left: 0;
        width: 8.2rem;

        .author-image {
          width: 100%;
        }
      }
    }
  }

  @media (min-width: 480px) {
    .author-wrapper {
      padding-top: 5.6rem;

      .author {
        padding-left: ${props => (props.hasMugshot ? "17.8rem" : "0")};

        .author-text {
          .written-by {
            position: relative;
            left: 0;
            text-indent: 0;
          }

          .author-name {
            padding-top: 1.2rem;
          }
        }

        .author-image-wrapper {
          top: 4.4rem;
          transform: translateY(-50%);
          width: 15rem;
        }
      }
    }
  }

  @media (min-width: 568px) {
    .author-wrapper {
      .author {
        .author-text {
          .socials {
            position: absolute;
            top: 1.8rem;
            right: 0;
          }
        }

        .author-image-wrapper {
          top: 50%;
        }
      }
    }
  }
`;

const Mugshot = ({
  data, alt, path
}: IMugshot) => {
  if (!data) {
    return null;
  }

  return (
    <div className="author-image-wrapper">
      <Link
        type="none"
        href={path}
      >
        <DynamicImage
          className="author-image"
          data={data}
          alt={alt}
        />
      </Link>
    </div>
  );
};

const MoreLink = ({
  type, authorName, path
}: IMoreLink) => {
  const firstName: string = authorName.split(" ")[ 0 ];
  const pathSplit = path.split("/");
  const nameSlug = pathSplit.reverse()[ 0 ];
  let href = "";
  let linkText = "";

  switch (type) {
    default:
    case "latest":
      href = `/latest/${nameSlug}`;
      linkText = `Read more in Latest by ${firstName}`;
      break;

    case "insights":
      href = `/insights/${nameSlug}`;
      linkText = `Read more in Insights by ${firstName}`;
      break;
  }

  return (
    <Link href={href}>
      {linkText}
    </Link>
  );
};

const ArticleAuthor = ({ type, profile }: IProps) => {
  const pr = profile.relationships;
  const hasMugshot = pr && pr.field_mugshot ? true : false;
  const mugshotData = pr && pr.field_mugshot ? pr.field_mugshot : undefined;

  return (
    <ArticleAuthorWrapper
      className="content-container author-container"
      hasMugshot={hasMugshot}
    >
      <SmallColumnWidthWrapper
        tagName="span"
        className="hr"
      >
        <span />
      </SmallColumnWidthWrapper>

      <HalfColumnWidthWrapper className="author-wrapper">
        <div className="author">
          <p className="author-text">
            <span className="written-by">
              Written by
              {" "}
            </span>

            <span className="author-name">
              <Link
                type="none"
                href={profile.path.alias}
              >
                {profile.field_full_name}
              </Link>
            </span>

            <span className="author-role">
              <span className="off-screen">
                {" "}
                (
              </span>

              {profile.field_job_title}

              <span className="off-screen">
                ).
                {" "}
              </span>
            </span>

            <span className="read-more">
              <MoreLink
                type={type}
                authorName={profile.field_full_name}
                path={profile.path.alias}
              />
            </span>

            <span className="off-screen">
              {" "}
              or check our their socials

              {" "}
            </span>

            <AuthorSocialLinks
              twitterUsername={profile.field_twitter_username}
              instagramUsername={profile.field_instagram_username}
            />
          </p>

          <Mugshot
            data={mugshotData}
            alt={profile.field_full_name}
            path={profile.path.alias}
          />
        </div>
      </HalfColumnWidthWrapper>
    </ArticleAuthorWrapper>
  );
};

export default ArticleAuthor;
